import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import BookingCalendar from '../../components/feature/BookingCalendar';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    loanInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d62uhnlsi3r5mpi9un2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          loanInterest: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-[#1A3A52]">
        <div className="max-w-4xl mx-auto text-center">
          <nav className="text-sm text-white/60 mb-4">
            <span>Home</span> <span className="mx-2">/</span> <span className="text-white">Contact Us</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get in Touch
          </h1>
          <p className="text-lg text-white/80">
            We're here to answer your questions and guide you
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Form - 60% */}
            <div className="lg:col-span-3">
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-6">
                Send Us a Message
              </h2>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm"
                    placeholder="+263 78 225 1180"
                  />
                </div>

                <div>
                  <label htmlFor="loanInterest" className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Interest
                  </label>
                  <select
                    id="loanInterest"
                    name="loanInterest"
                    value={formData.loanInterest}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">Select a loan type</option>
                    <option value="Salary-Based Loan">Salary-Based Loan</option>
                    <option value="SME & Working Capital">SME & Working Capital</option>
                    <option value="Microenterprise Loan">Microenterprise Loan</option>
                    <option value="Asset-Backed Loan">Asset-Backed Loan</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A3A52] focus:border-transparent text-sm resize-none"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                    <p className="text-green-800 text-sm">
                      Thank you for contacting us! We'll get back to you within 1-2 business days.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <i className="ri-error-warning-line text-red-600 text-xl"></i>
                    <p className="text-red-800 text-sm">
                      Something went wrong. Please try again or contact us directly.
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  variant="primary" 
                  size="large" 
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Information - 40% */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#1A3A52] mb-6">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#E8EFF5] rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-[#1A3A52]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A3A52] mb-1">Office Address</h3>
                      <p className="text-gray-600 text-sm">
                        56 Edmonds Ave, Belvedere, Harare, Zimbabwe
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#E8EFF5] rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-xl text-[#1A3A52]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A3A52] mb-1">Phone</h3>
                      <a href="tel:+263782251180" className="text-gray-600 text-sm hover:text-[#1A3A52] transition-colors cursor-pointer">
                        +263 78 225 1180
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#E8EFF5] rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-xl text-[#1A3A52]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A3A52] mb-1">Email</h3>
                      <a href="mailto:apply@nakhalatfinance.com" className="text-gray-600 text-sm hover:text-[#1A3A52] transition-colors cursor-pointer">
                        apply@nakhalatfinance.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#E8EFF5] rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-xl text-[#1A3A52]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A3A52] mb-1">Office Hours</h3>
                      <p className="text-gray-600 text-sm">
                        Monday - Friday: 8:00 AM - 5:00 PM<br />
                        Saturday: 8:00 AM - 1:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-gray-100 rounded-lg overflow-hidden h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3798.0!2d30.9748!3d-17.8252!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a4e4e5e2b5e5%3A0x5c7e3b5e5e5e5e5e!2s56%20Edmonds%20Ave%2C%20Belvedere%2C%20Harare%2C%20Zimbabwe!5e0!3m2!1sen!2s!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="Nakhalat Finance Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Calendar Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#E8EFF5]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#1A3A52] text-white text-sm font-medium rounded-full mb-4">
              Schedule a Consultation
            </span>
            <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">
              Book an Appointment
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Meet with our loan officers to discuss your financing needs. Select a convenient date and time for your free consultation.
            </p>
          </div>

          <BookingCalendar
            timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/81cf1c13-e87e-4284-a374-b5fbba62b3e4.823347cd1651e32ba33dbd030efc98c169d1638098e2bf8d85208eeaeb13f65c"
            appointmentsApi="https://readdy.ai/api/public/calendar/appointments/81cf1c13-e87e-4284-a374-b5fbba62b3e4.823347cd1651e32ba33dbd030efc98c169d1638098e2bf8d85208eeaeb13f65c"
            texts={{
              selectDate: 'Select Date & Time',
              selectTime: 'Select Time',
              reviewAndBook: 'Review & Book',
              previousMonth: 'Previous month',
              nextMonth: 'Next month',
              availableSlots: 'Available Times',
              noSlotsAvailable: 'No available slots for this date',
              selectedDateTime: 'Your Selected Appointment',
              modifyTime: 'Change Time',
              customerName: 'Full Name',
              customerPhone: 'Phone Number',
              loanType: 'Loan Type of Interest',
              notes: 'Additional Notes',
              submitBooking: 'Confirm Booking',
              submitting: 'Booking...',
              bookingSuccess: 'Your appointment has been booked successfully! We will contact you to confirm.',
              bookingError: 'Failed to book appointment. Please try again or contact us directly.',
              required: 'Required',
              back: 'Back',
              continue: 'Continue',
              step: 'Step',
              of: 'of'
            }}
          />

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              <i className="ri-information-line mr-1"></i>
              Consultations are free and typically last 30-45 minutes
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}